import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MdEdit } from "react-icons/md";
import Avatar from "components/avatar";
import Button from "components/button";
import SideDrawer from "components/drawer";
import Tabs, { TabPanel } from "components/tabs";
import { PersonBadges, personName } from "./components/PersonHeader";
import Details from "./tabs/details";
import HouseHold from "./tabs/houseHold";
import History from "./tabs/history";
import Groups from "./tabs/groups";

export const PERSON_TABS = [
  { id: "details", label: "Details" },
  { id: "household", label: "Household" },
  { id: "history", label: "History" },
  { id: "groups", label: "Groups" },
];

/**
 * Renders the content of one person tab.
 * @param {{tab: string, user: object}} props
 */
export const PersonTabContent = ({ tab, user }) => {
  switch (tab) {
    case "household":
      return <HouseHold user={user} />;
    case "history":
      return <History user={user} />;
    case "groups":
      return <Groups userId={user?.id} />;
    default:
      return <Details user={user} />;
  }
};

/**
 * Quick-look drawer for a person: avatar, badges and the profile tabs, with
 * links to the full profile and the edit page.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} [props.user] Person from the list. When the drawer is opened
 *   from a deep link without a user, it redirects to the full profile page.
 */
const UserDetailsDrawer = ({ isOpen, onClose, user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    if (isOpen && !user && id) {
      navigate(`/admin/people/${id}/profile`);
    }
  }, [isOpen, user, id, navigate]);

  useEffect(() => {
    if (isOpen) setActiveTab("details");
  }, [isOpen, user?.id]);

  return (
    <SideDrawer
      isOpen={isOpen && Boolean(user)}
      onClose={onClose}
      size="lg"
      title={personName(user) || "Person"}
      subtitle={user?.email}
      footer={
        user && (
          <>
            <Button
              as={Link}
              to={`/admin/people/${user.id}/edit`}
              variant="secondary"
              leftIcon={<MdEdit className="h-4 w-4" />}
            >
              Edit Profile
            </Button>
            <Button as={Link} to={`/admin/people/${user.id}/profile`}>
              View Full Profile
            </Button>
          </>
        )
      }
    >
      {user && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-4 self-start">
            <Avatar user={user} size="lg" noBorder />
            <PersonBadges user={user} />
          </div>
          <Tabs
            id="person-drawer-tabs"
            aria-label="Person sections"
            tabs={PERSON_TABS}
            value={activeTab}
            onChange={setActiveTab}
          />
          <TabPanel tabsId="person-drawer-tabs" tabId={activeTab}>
            <PersonTabContent tab={activeTab} user={user} />
          </TabPanel>
        </div>
      )}
    </SideDrawer>
  );
};

export default UserDetailsDrawer;
